import React, { createContext, useContext, useEffect } from 'react';
import Head from 'next/head';
import { TenantBranding, mergeBrandingWithDefaults, brandingToCSSProperties } from '../../lib/types/branding';

interface BrandContextValue {
  branding: TenantBranding;
  orgName: string;
  currency: string;
  timezone: string;
}

const BrandContext = createContext<BrandContextValue | null>(null);

interface BrandProviderProps {
  children: React.ReactNode;
  branding?: TenantBranding | null;
  orgName: string;
  currency?: string;
  timezone?: string;
}

export function BrandProvider({ 
  children, 
  branding, 
  orgName, 
  currency = 'GBP',
  timezone = 'Europe/London'
}: BrandProviderProps) {
  const mergedBranding = mergeBrandingWithDefaults(branding);
  const cssProperties = brandingToCSSProperties(mergedBranding);

  // Apply CSS custom properties to document root
  useEffect(() => {
    const root = document.documentElement;
    Object.entries(cssProperties).forEach(([property, value]) => {
      root.style.setProperty(property, value);
    });

    // Cleanup on unmount
    return () => {
      Object.keys(cssProperties).forEach((property) => {
        root.style.removeProperty(property);
      });
    };
  }, [cssProperties]);

  const contextValue: BrandContextValue = {
    branding: mergedBranding,
    orgName,
    currency,
    timezone,
  };

  return (
    <BrandContext.Provider value={contextValue}>
      <Head>
        {/* Favicon */}
        <link rel="icon" href={mergedBranding.faviconUrl || '/favicon.ico'} />
        <link rel="shortcut icon" href={mergedBranding.faviconUrl || '/favicon.ico'} />
        
        {/* Theme color for mobile browsers */}
        <meta name="theme-color" content={mergedBranding.primaryColor} />
        
        {/* Preload hero image if available */}
        {mergedBranding.heroImageUrl && (
          <link
            rel="preload"
            as="image"
            href={mergedBranding.heroImageUrl}
            media="(min-width: 768px)"
          />
        )}
      </Head>
      {children}
    </BrandContext.Provider>
  );
}

/**
 * Hook to access branding context
 */
export function useBranding(): BrandContextValue {
  const context = useContext(BrandContext);
  if (!context) {
    throw new Error('useBranding must be used within a BrandProvider');
  }
  return context;
}

/**
 * Hook to get organization-specific formatting functions
 */
export function useOrgFormatting() {
  const { currency, timezone } = useBranding();
  
  const formatPrice = (amount: number): string => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: currency,
    }).format(amount / 100); // Assuming amounts are in cents
  };

  const formatDate = (date: Date | string, options?: Intl.DateTimeFormatOptions): string => {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    return new Intl.DateTimeFormat('en-GB', {
      timeZone: timezone,
      ...options,
    }).format(dateObj);
  };

  return { formatPrice, formatDate };
}









